import {useEffect,useRef,useState} from 'react';
import {LoaderCircle} from 'lucide-react';
import type {EmailRegistrationChallenge,User} from '@reados/contracts';
import {api,errorMessage} from '../api';
import {AuthOperationScope,emailRegistrationRequiresLogin,emailRegistrationTimers,registrationTimeLabel} from '../email-registration';
import {useAuthProviders} from '../hooks/useAuthProviders';
import {GoogleLoginButton,useGoogleAuth} from './GoogleAuth';
import appIcon from '../assets/branding/reados-app-icon-128.png';
import './auth-brand.css';
import './email-verification.css';

type AuthMode='login'|'register'|'verify';
type AuthBusy='login'|'request'|'verify'|'resend'|null;

export function Auth({onUser,initialError}:{onUser:(user:User)=>void;initialError?:string}){
 const [mode,setMode]=useState<AuthMode>('login'),[busy,setBusy]=useState<AuthBusy>(null),[error,setError]=useState(initialError||''),[notice,setNotice]=useState('');
 const [draft,setDraft]=useState({name:'',email:'',password:''}),[code,setCode]=useState(''),[challenge,setChallenge]=useState<EmailRegistrationChallenge|null>(null),[now,setNow]=useState(Date.now());
 const operations=useRef(new AuthOperationScope()),mounted=useRef(true),codeInput=useRef<HTMLInputElement>(null);
 const {providers,loading:providersLoading,error:providersError,reload}=useAuthProviders(),google=useGoogleAuth(setError),disabled=busy!==null||google.busy;
 const emailAvailable=providers?.registrationEnabled===true&&providers.email?.enabled===true;
 const timers=challenge?emailRegistrationTimers(challenge,now):null;

 useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;operations.current.invalidate();};},[]);
 useEffect(()=>{
  if(!challenge)return;
  setNow(Date.now());const interval=setInterval(()=>setNow(Date.now()),1000);
  return()=>clearInterval(interval);
 },[challenge]);
 useEffect(()=>{if(mode==='verify'&&!disabled)codeInput.current?.focus();},[mode,disabled]);
 useEffect(()=>{
  if(providers?.registrationEnabled===false&&mode!=='login'&&busy!=='verify'){
   operations.current.invalidate();setMode('login');setChallenge(null);setCode('');
  }
 },[providers?.registrationEnabled,mode,busy]);

 function changeMode(next:AuthMode){
  operations.current.invalidate();setMode(next);setChallenge(null);setCode('');setError('');setNotice('');
 }
 function updateDraft(field:keyof typeof draft,value:string){setDraft(previous=>({...previous,[field]:value}));}

 async function submit(event:React.FormEvent<HTMLFormElement>){
  event.preventDefault();if(disabled)return;
  if(mode==='register'&&!emailAvailable){setError('邮箱注册暂时不可用，请稍后重试。');return;}
  if(mode==='verify'&&(!challenge||!timers?.expiresIn)){setError('验证码已过期，请返回重新获取。');return;}
  const operation=operations.current.begin();if(!operation)return;
  const action=mode==='login'?'login':mode==='register'?'request':'verify';setBusy(action);setError('');setNotice('');
  try{
   if(action==='request'){
    const receipt=await api.requestEmailRegistration(draft.email.trim(),draft.password,draft.name);
    if(!mounted.current||!operations.current.current(operation))return;
    emailRegistrationTimers(receipt);setChallenge(receipt);setCode('');setNow(Date.now());setMode('verify');
   }else{
    const result=action==='verify'?await api.verifyEmailRegistration(challenge!.challengeId,code):await api.login(draft.email.trim(),draft.password);
    if(mounted.current&&operations.current.current(operation))onUser(result.user);
   }
  }catch(value){
   if(mounted.current&&operations.current.current(operation)){
    if(emailRegistrationRequiresLogin(action,value)){
     operations.current.invalidate();setMode('login');setChallenge(null);setCode('');setNotice('');
    }
    setError(errorMessage(value));
   }
  }
  finally{if(operations.current.finish(operation)&&mounted.current)setBusy(null);}
 }

 async function resend(){
  if(disabled||!challenge||!timers?.expiresIn||timers.resendIn>0||!emailAvailable)return;
  const operation=operations.current.begin();if(!operation)return;
  setBusy('resend');setError('');setNotice('');
  try{
   const receipt=await api.resendEmailRegistration(challenge.challengeId);
   if(!mounted.current||!operations.current.current(operation))return;
   emailRegistrationTimers(receipt);setChallenge(receipt);setNow(Date.now());setNotice('重新发送请求已提交，请查收。验证码有效期不变。');
  }catch(value){if(mounted.current&&operations.current.current(operation))setError(errorMessage(value));}
  finally{if(operations.current.finish(operation)&&mounted.current)setBusy(null);}
 }

 const submitLabel=mode==='login'?'登录':mode==='register'?'发送验证码':'验证并创建账号';
 const busyLabel=busy==='login'?'正在登录…':busy==='request'?'正在发送验证码…':busy==='resend'?'正在重新发送…':'正在验证邮箱…';
 return <main className="auth-page"><form className="auth-form" onSubmit={submit}>
  <h1 className="auth-brand"><img src={appIcon} width={40} height={40} alt="" aria-hidden="true"/>ReadOS</h1>
  <h2>{mode==='login'?'登录':mode==='register'?'创建账号':'验证邮箱'}</h2>
  {mode==='verify'&&challenge&&timers?<>
   <p className="auth-verification-description">请在以下邮箱查收验证码：<strong>{draft.email.trim()}</strong>请检查收件箱和垃圾邮件。</p>
   <label htmlFor="auth-email-code">邮箱验证码<input ref={codeInput} id="auth-email-code" name="code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={event=>setCode(event.target.value.replace(/\D/g,''))} disabled={disabled} aria-describedby="auth-code-validity" placeholder="6 位数字"/></label>
   <p id="auth-code-validity" className="auth-verification-timer" aria-live={timers.expiresIn>0?'off':'polite'}>{timers.expiresIn>0?`验证码在 ${registrationTimeLabel(timers.expiresIn)} 后过期`:'验证码已过期，请返回重新获取。'}</p>
  </>:<>
   {mode==='register'&&<label>姓名<input name="name" autoComplete="name" required maxLength={80} value={draft.name} onChange={event=>updateDraft('name',event.target.value)} disabled={disabled}/></label>}
   <label>邮箱<input name="email" type="email" autoComplete="email" required value={draft.email} onChange={event=>updateDraft('email',event.target.value)} disabled={disabled}/></label>
   <label>密码<input name="password" type="password" autoComplete={mode==='register'?'new-password':'current-password'} minLength={mode==='register'?12:1} required placeholder={mode==='register'?'至少 12 个字符':''} value={draft.password} onChange={event=>updateDraft('password',event.target.value)} disabled={disabled}/></label>
   {mode==='register'&&<p className="auth-verification-description">验证邮箱后创建账号并领取初始额度。</p>}
  </>}
  {error&&<div className="form-error" role="alert">{error}</div>}
  {notice&&<p className="auth-verification-description" role="status">{notice}</p>}
  <button className="primary auth-submit" disabled={disabled||(mode==='register'&&!emailAvailable)||(mode==='verify'&&!timers?.expiresIn)} aria-busy={busy==='login'||busy==='request'||busy==='verify'}>{busy&&busy!=='resend'?<><LoaderCircle size={17} className="spin" aria-hidden="true"/><span>{busyLabel}</span></>:submitLabel}</button>
  {mode==='verify'&&timers?<div className="auth-verification-actions"><button type="button" className="text-button" disabled={disabled||!timers.expiresIn||timers.resendIn>0||!emailAvailable} onClick={()=>void resend()} aria-busy={busy==='resend'}>{busy==='resend'?'正在重新发送…':timers.resendIn>0?`${timers.resendIn} 秒后可重新发送`:'重新发送验证码'}</button><button type="button" className="text-button" disabled={google.busy||busy==='verify'} onClick={()=>changeMode('register')}>{timers.expiresIn>0?'修改邮箱':'返回重新获取'}</button></div>:<>
   {providers?.google.enabled&&<><div className="auth-email-divider">或</div><GoogleLoginButton busy={google.busy} disabled={busy!==null} onClick={()=>void google.begin('login')}/></>}
   {providers?.registrationEnabled!==false&&<div className="auth-switch">{mode==='register'?'已有账号':'没有账号'}<button type="button" className="text-button" disabled={google.busy||busy==='login'||(mode==='login'&&(busy!==null||!emailAvailable))} onClick={()=>changeMode(mode==='register'?'login':'register')}>{mode==='register'?'登录':'创建账号'}</button></div>}
  </>}
  {providersLoading&&<p className="auth-provider-error" role="status">正在读取注册方式…</p>}
  {!providersLoading&&!providersError&&providers?.registrationEnabled!==false&&!emailAvailable&&<p className="auth-provider-error" role="status">邮箱注册暂未开放，请稍后再试。</p>}
  {providersError&&<div className="auth-provider-error" role="status"><span>{providersError}</span><button type="button" className="text-button" onClick={reload}>重试</button></div>}
 </form></main>;
}
